/**
 * useMapInteractions.ts
 * Base interaction handling for map
 */

import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Map, MapGeoJSONFeature, PaddingOptions } from "maplibre-gl";
import {
  Feature,
  FeatureCollection,
  Geometry,
  GeometryCollection
} from "geojson";

import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  POLYGON_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import { SUBLAYERS } from "@/constants/map";
import { RootState, store } from "@/store";
import { addGridSelected } from "@/store/slice/analysisSummary";
import {
  addComparisonFeature,
  setSelectedFeature
} from "@/store/slice/mapSlice";
import { toggleComparisonLimitShow } from "@/store/slice/uiSlice";
import { AdminAreaData } from "@/types/adminArea";
import { BoundingBox, SelectedFeature } from "@/types/map";
import { calculateFeatureBounds } from "@/utils/common";

import { bbox, featureCollection, pointOnFeature } from "@turf/turf";

import { DEFAULT_ZOOM } from "../core/useMapInitialization";
import { findClickableFeature, isFeatureAlreadyActive } from "./helpers";

export const BOUNDS_PADDING = 50; // General padding in pixels to prevent features from rendering too close to the edge, especially on high-DPI or small screens
export const MAX_ZOOM = 16;
export const TO_BOUNDS_DURATION = 1000; // milliseconds;

// Extended Feature type that includes maplibre-specific properties
interface MapFeature extends Feature<Geometry> {
  source?: string;
  id?: string | number;
}

type UseMapInteractionsProps = {
  map: Map;
  handleClearSelectedState: (feature: SelectedFeature) => void;
  handleHighlightGridSelection: (sourceId: string, gridIds: string[]) => void;
};
/**
 * Hook for managing map interactions
 */
export const useMapInteractions = ({
  map,
  handleClearSelectedState,
  handleHighlightGridSelection
}: UseMapInteractionsProps) => {
  const dispatch = useDispatch();
  const { gridsSelected, spatialAnalysisDatasetId } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  const handleMovePositionToLayer = (
    polygonData: FeatureCollection<Geometry>,
    options?: {
      padding?: PaddingOptions | number;
      offset?: [number, number]; // [x, y] offset in pixels
      zoom?: number; // Custom zoom level
    }
  ) => {
    if (!map) return;

    const bounds = calculateFeatureBounds(polygonData);

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, {
        padding: options?.padding ?? BOUNDS_PADDING,
        maxZoom: options?.zoom ?? MAX_ZOOM,
        duration: TO_BOUNDS_DURATION,
        ...(options?.offset && { offset: options.offset })
      });
    }
  };

  const clickHandler = (
    e: maplibregl.MapMouseEvent & {
      features?: MapFeature[];
    }
  ) => {
    const features = map.queryRenderedFeatures(e.point);

    if (!map || !e.features || e.features.length === 0) return;

    // Get the first clickable feature in map if the feature is overlayed by other features
    // E.g. A POI point should be the clicked feature from the map when it gets overlayed by admin area, catchment radius, or isochrone radius
    const clickedFeature = findClickableFeature(features);

    if (!clickedFeature) return;

    const currentState = store.getState();
    const { selectedFeature, comparisonFeatureData } = currentState.map;
    const { showComparisonPopup, showAnalysisSummaryPopup } = currentState.ui;

    if (showAnalysisSummaryPopup) {
      clickHandlerSummaryAnalysis(clickedFeature);
      return;
    }

    if (showComparisonPopup.isShow) {
      handleComparisonModeClick(
        clickedFeature,
        comparisonFeatureData,
        selectedFeature
      );
      return;
    }

    handleNormalModeClick(clickedFeature, selectedFeature);
  };

  const handleComparisonModeClick = (
    feature: MapFeature,
    comparisonFeatures: SelectedFeature[],
    currentSelection: SelectedFeature | null
  ) => {
    // Early return: feature already selected
    if (isFeatureAlreadyActive(feature, currentSelection, comparisonFeatures)) {
      return;
    }

    // Early return: limit reached
    if (comparisonFeatures.length > 3) {
      dispatch(toggleComparisonLimitShow());
      return;
    }

    // Add to comparison
    map.setFeatureState(
      { source: feature.source as string, id: feature.id },
      { selected: true }
    );

    dispatch(
      addComparisonFeature({
        properties: feature.properties,
        id: feature.id ?? "",
        source: feature.source ?? "",
        geometry: feature.geometry as Exclude<Geometry, GeometryCollection>
      })
    );
  };

  const handleNormalModeClick = (
    feature: MapFeature,
    currentSelection: SelectedFeature | null
  ) => {
    // Clear previous selection if exists
    if (currentSelection?.id === 0 || !!currentSelection?.id) {
      handleClearSelectedState(currentSelection);
    }

    // Set new selection
    map.setFeatureState(
      { source: feature.source as string, id: feature.id },
      { selected: true }
    );

    dispatch(
      setSelectedFeature({
        properties: feature.properties,
        id: feature.id ?? "",
        source: feature.source ?? "",
        geometry: feature.geometry as Exclude<Geometry, GeometryCollection>
      })
    );
  };

  const clickHandlerSummaryAnalysis = (clickedFeature?: MapGeoJSONFeature) => {
    if (!clickedFeature) return;
    const gridOrCatchmentId =
      clickedFeature?.properties[GRID_ID_ATTRIBUTE] ??
      clickedFeature?.properties[CATCHMENT_ID_ATTRIBUTE] ??
      clickedFeature?.properties[POLYGON_ID_ATTRIBUTE];

    if (gridOrCatchmentId) dispatch(addGridSelected(gridOrCatchmentId));
  };

  const handleFeatureClick = (layerId: string): void => {
    if (!map) return;
    map.on("click", layerId, clickHandler);
  };

  const fitMapToLayerBound = useCallback(
    (items: AdminAreaData) => {
      if (!map) return;

      if (items?.data?.length) {
        const allFeatures = items.data.flatMap(
          (item) => item.boundary.features
        );
        const mergedFeatureCollection = featureCollection(allFeatures);

        const combinedBbox = bbox(mergedFeatureCollection);

        map.fitBounds(combinedBbox as BoundingBox, {
          padding: BOUNDS_PADDING
        });
      }
    },
    [map]
  );

  /**
   * Resets the map view to its default orientation (bearing: 0, pitch: 0) and default zoom level (4),
   * while preserving the current center position.
   */
  const resetMapView = () => {
    if (!map) return;

    map.flyTo({
      zoom: DEFAULT_ZOOM,
      duration: TO_BOUNDS_DURATION,
      bearing: 0,
      pitch: 0
    });
  };

  const resetAllFeatureStates = useCallback(() => {
    if (!map) return;

    const style = map.getStyle();
    if (!style?.sources) return;

    for (const [sourceId, source] of Object.entries(style.sources)) {
      if (source.type === "geojson") {
        map.removeFeatureState({ source: sourceId });
      }
    }
  }, [map]);

  const handleHighlightFeatureByGeometry = useCallback(
    (sourceId: string, geometry: Geometry) => {
      if (!map || !geometry || !sourceId) return;

      const insidePoint = pointOnFeature(geometry).geometry.coordinates;
      const [lng, lat] = insidePoint;

      const geometryBounds = bbox(geometry) as BoundingBox;

      map.fitBounds(geometryBounds, {
        padding: BOUNDS_PADDING,
        duration: TO_BOUNDS_DURATION
      });

      map.once("idle", () => {
        const pixelPoint = map.project([lng, lat]);
        const features = map.queryRenderedFeatures(pixelPoint);
        const target =
          features.find((f) => f.source === sourceId) ?? features[0];
        if (!target) return;

        const featureId = target.id;
        const getAllSublayerSources = (datasetId: string) => {
          const config = SUBLAYERS(datasetId);
          return Object.values(config).flatMap((group) => Object.values(group));
        };

        const possibleSources = getAllSublayerSources(sourceId);

        const baseSource = possibleSources.includes(target.source)
          ? target.source
          : sourceId;

        if (featureId !== undefined) {
          map.setFeatureState(
            { source: baseSource, id: featureId },
            { selected: true }
          );
        }

        dispatch(
          setSelectedFeature({
            properties: target.properties,
            id: target.id ?? "",
            source: target.source ?? "",
            geometry: target.geometry as Exclude<Geometry, GeometryCollection>
          })
        );
      });
    },
    [dispatch, map]
  );

  // Highlight grids when gridsSelected changes
  useEffect(() => {
    if (!map || !spatialAnalysisDatasetId) return;

    const runHighlightSelection = () => {
      handleHighlightGridSelection(spatialAnalysisDatasetId, gridsSelected);
    };

    if (map.isStyleLoaded()) {
      runHighlightSelection();
    } else {
      map.once("idle", runHighlightSelection);
    }

    return () => {
      map.off("idle", runHighlightSelection);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, spatialAnalysisDatasetId, gridsSelected]);

  return {
    handleHighlightFeatureByGeometry,
    fitMapToLayerBound,
    handleFeatureClick,
    handleMovePositionToLayer,
    resetAllFeatureStates,
    resetMapView
  };
};

export default useMapInteractions;
